import { tetriToDecimalString } from '@/utils/money';
import { ACCOUNT_ROLES } from '@/api/models';
import type {
    AccountBalance,
    AccountDetail,
    AccountListItem,
    AccountRole,
    AccountRoleTag,
    AccountType,
    LocaleCode,
    NormalBalance,
} from '@/api/models';

/**
 * Wire ⇄ domain mapping for ledger accounts (bos-api, BOS-91).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — services, hooks, the tree builder, the tie-outs — sees the domain
 * shapes in `@/api/models`. If the wire disagrees with what is assumed here, this
 * file is the whole of the fix.
 *
 * The assumptions, all taken from the contract's own vocabulary and each one
 * checkable against a single live response:
 *
 * | domain | wire |
 * | --- | --- |
 * | `archived` | `archived` (true = hidden). **Never `active`** — see `models/Accounts` |
 * | `hasPostings` | `hasPostings` |
 * | `etag` | `etag` on the body (a top-level field, not the HTTP header) |
 * | `name` (list) | `name` — already resolved for the requested `locale` |
 * | `name` (detail) | `name` — an object keyed by locale |
 * | balance | `signedBalanceTetri` + `signedBalance`, optionally nested under `balance` |
 * | `bootstrapRequired` | `bootstrapRequired` on the list wrapper |
 * | `accountRole` | `accountRole` (BOS-107). `'NONE'` and absent both read as `null` |
 *
 * Two deliberate pieces of slack, because they cost nothing and remove a whole
 * class of integration failure:
 *
 * 1. **Balance shape.** Both a flat row (`signedBalanceTetri` on the account) and
 *    a nested `balance: { … }` are accepted.
 * 2. **Natural balance is derived when absent.** The contract ships
 *    `signedBalanceTetri` as authoritative; `natural` is just the same number
 *    read in the account's own direction, so it is computed from `normalBalance`
 *    rather than required on the wire. The tie-outs depend on `natural` (a
 *    credit-normal contra must report a positive magnitude), so it must always be
 *    present downstream — deriving it guarantees that.
 *
 * A row that cannot be mapped is **dropped, not guessed at**: an account with an
 * unrecognised `type` or `normalBalance` would be rendered in the wrong section
 * of the chart or summed on the wrong side, and a silently misplaced account is
 * worse than a missing one. `mapAccountList` reports what it dropped.
 */

const ACCOUNT_TYPES: readonly string[] = [
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'REVENUE',
    'EXPENSE',
];

const isAccountType = (value: unknown): value is AccountType =>
    typeof value === 'string' && ACCOUNT_TYPES.includes(value);

const isNormalBalance = (value: unknown): value is NormalBalance =>
    value === 'DEBIT' || value === 'CREDIT';

const isAccountRole = (value: unknown): value is AccountRole =>
    (ACCOUNT_ROLES as readonly string[]).includes(value as string);

/**
 * Normalise the wire's role tag.
 *
 * Unlike `type` and `normalBalance`, an unrecognised role does **not** drop the
 * row — it reads as untagged. A role the client does not know about is one the
 * server added for a flow this client has no part in; dropping the account over
 * it would hide a real account from the chart to protect a field the screen only
 * displays. `'NONE'` and an absent tag both normalise to `null` so downstream
 * code has one "untagged" value to test rather than two.
 */
const toAccountRole = (value: unknown): AccountRoleTag =>
    isAccountRole(value) && value !== 'NONE' ? value : null;

/** A balance envelope as the wire may carry it — flat, nested, or absent. */
type BalanceWire = {
    signedBalanceTetri?: number | null;
    signedBalance?: string | null;
    naturalBalanceTetri?: number | null;
    naturalBalance?: string | null;
};

export type AccountWire = BalanceWire & {
    id?: string;
    code?: string;
    name?: unknown;
    type?: string;
    parentId?: string | null;
    normalBalance?: string;
    isContra?: boolean | null;
    isPostable?: boolean | null;
    vatRole?: string | null;
    accountRole?: string | null;
    archived?: boolean | null;
    hasPostings?: boolean | null;
    etag?: string | null;
    statementMapping?: string | null;
    cashFlowClass?: string | null;
    balance?: BalanceWire | null;
};

export type AccountListWire = {
    items?: AccountWire[] | null;
    bootstrapRequired?: boolean | null;
};

/**
 * Build the domain balance from whichever form the wire used.
 *
 * Returns `null` only when the wire carried no signed figure at all — which is a
 * real answer ("this account has never been posted to or seeded"), distinct from
 * a balance of zero. The tree renders `null` and `0` identically today, but the
 * archive gate and the tie-outs must not treat "unknown" as "zero", so the
 * distinction is preserved rather than flattened here.
 */
const mapBalance = (
    accountId: string,
    wire: AccountWire,
    normalBalance: NormalBalance,
): AccountBalance | null => {
    const source: BalanceWire = wire.balance ?? wire;
    const signedTetri = source.signedBalanceTetri;
    if (typeof signedTetri !== 'number' || !Number.isFinite(signedTetri)) {
        return null;
    }

    // `natural` is the same position read in the account's own direction. Derive
    // it whenever the wire omits it — the tie-outs cannot work without it.
    const naturalTetri =
        typeof source.naturalBalanceTetri === 'number' &&
        Number.isFinite(source.naturalBalanceTetri)
            ? source.naturalBalanceTetri
            : normalBalance === 'CREDIT'
              ? -signedTetri
              : signedTetri;

    return {
        accountId,
        signedBalanceTetri: signedTetri,
        signedBalance:
            source.signedBalance ?? tetriToDecimalString(signedTetri),
        naturalBalanceTetri: naturalTetri,
        naturalBalance:
            source.naturalBalance ?? tetriToDecimalString(naturalTetri),
    };
};

/** The fields a list row and a detail record share, or `null` if unmappable. */
const mapCommon = (wire: AccountWire) => {
    const { id, code, type, normalBalance, etag } = wire;
    if (
        typeof id !== 'string' ||
        id === '' ||
        typeof code !== 'string' ||
        !isAccountType(type) ||
        !isNormalBalance(normalBalance)
    ) {
        return null;
    }

    return {
        id,
        code,
        type,
        normalBalance,
        parentId: wire.parentId ?? null,
        isContra: wire.isContra === true,
        isPostable: wire.isPostable === true,
        vatRole: wire.vatRole ?? null,
        accountRole: toAccountRole(wire.accountRole),
        // Absent reads as archived, not as live. A missing flag must never
        // promote a hidden account into the working chart.
        archived: wire.archived !== false,
        // Absent reads as "has postings", so the structural fields stay locked.
        // Failing closed here can only ever refuse an edit the server would have
        // refused anyway; failing open offers one the server will reject.
        hasPostings: wire.hasPostings !== false,
        etag: typeof etag === 'string' ? etag : '',
        ...(wire.statementMapping !== undefined
            ? { statementMapping: wire.statementMapping }
            : {}),
        ...(wire.cashFlowClass !== undefined
            ? { cashFlowClass: wire.cashFlowClass }
            : {}),
    };
};

/** One `GET /accounts` row → an {@link AccountListItem}, or `null` if unmappable. */
export const mapAccountListItem = (
    wire: AccountWire,
): AccountListItem | null => {
    const common = mapCommon(wire);
    if (!common) {
        return null;
    }
    // The list resolves the name server-side. A non-string (or an object, if the
    // server ignored `locale`) falls back to the code rather than rendering
    // "[object Object]" — the same last resort the client's own chain uses.
    const name =
        typeof wire.name === 'string' && wire.name !== ''
            ? wire.name
            : common.code;

    return {
        ...common,
        name,
        balance: mapBalance(common.id, wire, common.normalBalance),
    };
};

export type MapAccountListResult = {
    items: AccountListItem[];
    bootstrapRequired: boolean;
    /** Ids (or codes) of rows that could not be mapped, for the console warning. */
    dropped: string[];
};

export const mapAccountList = (
    wire: AccountListWire | null | undefined,
): MapAccountListResult => {
    const items: AccountListItem[] = [];
    const dropped: string[] = [];
    for (const row of wire?.items ?? []) {
        const mapped = mapAccountListItem(row);
        if (mapped) {
            items.push(mapped);
        } else {
            dropped.push(row?.id ?? row?.code ?? '(unidentified row)');
        }
    }
    return {
        items,
        bootstrapRequired: wire?.bootstrapRequired === true,
        dropped,
    };
};

/** A locale map from the detail endpoint, keeping only the locales we render. */
const mapNameMap = (value: unknown): Partial<Record<LocaleCode, string>> => {
    if (typeof value === 'string') {
        // A server that ignored the detail contract and resolved the name anyway:
        // better to seed both fields with it than to open an empty edit form.
        return { ka: value, en: value };
    }
    if (typeof value !== 'object' || value === null) {
        return {};
    }
    const record = value as Record<string, unknown>;
    const name: Partial<Record<LocaleCode, string>> = {};
    for (const locale of ['ka', 'en'] as const) {
        const localized = record[locale];
        if (typeof localized === 'string' && localized !== '') {
            name[locale] = localized;
        }
    }
    return name;
};

/** `GET /accounts/{id}` → an {@link AccountDetail}, or `null` if unmappable. */
export const mapAccountDetail = (
    wire: AccountWire | null | undefined,
): AccountDetail | null => {
    if (!wire) {
        return null;
    }
    const common = mapCommon(wire);
    if (!common) {
        return null;
    }
    return {
        ...common,
        name: mapNameMap(wire.name),
        balance: mapBalance(common.id, wire, common.normalBalance),
    };
};
